import type {
  ActResult,
  AckResult,
  BoardStatus,
  BriefView,
  HealthAccessResult,
  HealthShowResult,
  MadeTicket,
  MentionResult,
  MyHealthView,
  MyBoardView,
  ProposalDecisionPayload,
  ProposalDecisionResult,
  ProposalsStatus,
  ProposalView,
  StandupHostView,
  StandupMsg,
  StandupPublic,
  TeamBoardLive,
  TeamHealthView,
  TicketAction,
  TicketMention,
  TicketSpotlight,
  TicketView,
} from '@live-stream/shared'
import type { Room } from '../room.js'
import type { AppSocket } from '../socket.js'
import { derived, patch, readonly, watch, writable, type ReadableStore } from '../store.js'

/**
 * A team's Jira board in the meeting, this browser's side (B1). A host who leads the team loads it;
 * each person who is linked to a Jira person is then sent their own tickets, and a host who may is
 * sent the whole team. The server sends each view to this browser alone; nothing here is in the room's
 * shared state.
 *
 * While this person is presenting their screen, `hidden` is true and the panels should not draw: a
 * project manager sharing a screen must not show the team's board to everyone watching.
 */

export interface BoardState {
  /** This person's own tickets, or null when none were sent (not linked, or nothing loaded). */
  mine: MyBoardView | null
  /** The whole team, for a host who may see it. */
  team: TeamBoardLive | null
  /** What this person may do: the teams they may load, and what is loaded. Null until asked. */
  status: BoardStatus | null
  /** Cards for tickets of theirs that came up in the meeting, newest first, until dismissed. */
  tickets: TicketMention[]
  /** The ticket the host put on screen for everyone, if any. */
  spotlight: TicketSpotlight | null
  /** A ticket opened in the call, read live from Jira. Null when none is open. */
  detail: { key: string; loading: boolean; data: TicketView | null; error: string | null } | null
  /** The last change this person made from the call, for the undo bar. Null once undone, dismissed or too old. */
  lastChange: { id: string; label: string; undoable: boolean; at: number } | null
  /** A change is being made (or undone). */
  acting: boolean
  /** Why the last change did not go through, in words. */
  actionError: string | null
  /** This person's own board health, once they opened it or a host showed it to them. Holds nothing about anyone else. */
  health: MyHealthView | null
  /** The team's board health, for whoever leads the team and the one person the host named. */
  healthTeam: TeamHealthView | null
  /** A health call is under way. */
  healthBusy: boolean
  /** Why the last health call did not go through, in words; or what a show came to. */
  healthMessage: string | null
  /** Action items made into tickets, by item id: the ticket's key, and how to undo it while it can be. */
  madeTickets: Record<string, { key: string; actionId: string | null; undoable: boolean }>
  /** The action item a ticket is being made from now. */
  making: string | null
  /** Why the last ticket was not made, in words. */
  makeError: string | null
  /** The brief: what the board has to say, for a host who leads the team. Null until opened. */
  brief: BriefView | null
  /** The brief is being read. */
  briefBusy: boolean
  /** Why it could not be made, in words. */
  briefError: string | null
  /** The stand-up being walked: whose turn it is and when it is up. Null when none. Names no order. */
  standup: StandupPublic | null
  /** It is this person's turn. */
  yourTurn: boolean
  /** The order and what to ask about, for the hosts who may see the team. */
  standupHost: StandupHostView | null
  /** The updates the AI suggests that this person may decide on (all for a host who sees the team, else their own tickets'). */
  proposals: ProposalView[]
  /** The suggestion being decided on now, by id. */
  deciding: string | null
  /** Why a decision did not go through, in words. */
  proposalError: string | null
  /** What an approved blocker led to: who was told they are being waited on. */
  proposalNote: string | null
  /** Counts up whenever a ticket comes to this person (a card, or one put on screen): the panel opens on it. */
  attention: number
  busy: boolean
  error: string | null
}

const WAIT_MS = 20_000
/** The most ticket cards shown at once. */
const MAX_CARDS = 3

export class MeetingBoard {
  private readonly store = writable<BoardState>({
    mine: null,
    team: null,
    status: null,
    tickets: [],
    spotlight: null,
    detail: null,
    madeTickets: {},
    making: null,
    makeError: null,
    brief: null,
    briefBusy: false,
    briefError: null,
    standup: null,
    yourTurn: false,
    standupHost: null,
    health: null,
    healthTeam: null,
    healthBusy: false,
    healthMessage: null,
    proposals: [],
    deciding: null,
    proposalError: null,
    proposalNote: null,
    lastChange: null,
    acting: false,
    actionError: null,
    attention: 0,
    busy: false,
    error: null,
  })
  readonly state: ReadableStore<BoardState> = readonly(this.store)
  /** There is a board to show this person: their tickets, or the team's. */
  readonly present: ReadableStore<boolean>
  /** This person may load a team's board here. */
  readonly canLoad: ReadableStore<boolean>
  /** This person is presenting their screen: the panels hide until they stop. */
  readonly hidden: ReadableStore<boolean>

  private boundTo: AppSocket | null = null

  constructor(private readonly room: Room) {
    const r = room.state
    this.present = derived([this.store], (s) => s.mine !== null || s.team !== null || s.tickets.length > 0 || s.spotlight !== null || s.proposals.length > 0 || s.health !== null || s.healthTeam !== null || s.standup !== null || (s.status?.health.mine ?? false) || (s.status?.health.team ?? false))
    this.canLoad = derived([this.store], (s) => (s.status?.teams.length ?? 0) > 0)
    this.hidden = derived([r], (s) => s.me !== null && s.room.screenShare?.peerId === s.me)

    watch(
      room.socket,
      (s) => s,
      (sock) => this.bind(sock),
      { immediate: true },
    )
    // On joining and every rejoin, and when this person's role changes (a host may load a board).
    watch(
      r,
      (s) => `${s.phase}|${s.sessionId}|${s.rejoinCount}|${s.myRole}`,
      (_next, prev) => {
        if (r.get().phase === 'joined') void this.refresh()
        else if (prev?.startsWith('joined|')) this.reset()
      },
      { immediate: true },
    )
  }

  private readonly onMine = (msg: { board: MyBoardView | null }): void => {
    patch(this.store, { mine: msg.board })
  }
  private readonly onTicket = (msg: TicketMention): void => {
    // Newest first; a ticket already shown is replaced, not stacked; a few at most.
    const rest = this.store.get().tickets.filter((t) => t.card.key !== msg.card.key)
    patch(this.store, { tickets: [msg, ...rest].slice(0, MAX_CARDS), attention: this.store.get().attention + 1 })
  }
  private readonly onProposals = (msg: { proposals: ProposalView[] }): void => {
    const before = this.store.get().proposals.filter((p) => p.status === 'pending').length
    const now = msg.proposals.filter((p) => p.status === 'pending').length
    // More waiting than before: something new for this person to decide on.
    patch(this.store, { proposals: msg.proposals, ...(now > before ? { attention: this.store.get().attention + 1 } : {}) })
  }
  private readonly onSpotlight = (msg: { spotlight: TicketSpotlight | null }): void => {
    patch(this.store, {
      spotlight: msg.spotlight,
      ...(msg.spotlight ? { attention: this.store.get().attention + 1 } : {}),
    })
  }
  private readonly onStandup = (msg: StandupMsg): void => {
    const was = this.store.get().yourTurn
    // Your turn has come: the panel is brought to you.
    patch(this.store, { standup: msg.standup, yourTurn: msg.yourTurn, ...(msg.yourTurn && !was ? { attention: this.store.get().attention + 1 } : {}) })
    if (msg.standup === null) patch(this.store, { standupHost: null })
  }
  private readonly onStandupHost = (msg: { host: StandupHostView | null }): void => {
    patch(this.store, { standupHost: msg.host })
  }
  private readonly onHealth = (msg: { health: MyHealthView | null }): void => {
    const was = this.store.get().health
    // A host showing it to this person brings the panel to them; a refresh of what is open does not.
    const shown = msg.health !== null && msg.health.shownBy !== null && was?.shownBy !== msg.health.shownBy
    patch(this.store, { health: msg.health, ...(shown ? { attention: this.store.get().attention + 1 } : {}) })
    // The offer may have changed with it (the link, the board): look again.
    if (msg.health === null) void this.refresh()
  }
  private readonly onHealthTeam = (msg: { health: TeamHealthView | null }): void => {
    const was = this.store.get().healthTeam
    patch(this.store, { healthTeam: msg.health, ...(msg.health && !was ? { attention: this.store.get().attention + 1 } : {}) })
    // Named to see it, or no longer: the offer follows (a refresh of what is open changes nothing).
    if (!!msg.health !== !!was) void this.refresh()
  }
  private readonly onTeam = (msg: { board: TeamBoardLive | null }): void => {
    patch(this.store, { team: msg.board })
    // The board was read anew: an open brief follows it; one with no board to speak of goes.
    if (!msg.board) patch(this.store, { brief: null })
    else if (this.store.get().brief) void this.openBrief()
    // What is loaded changed under the host (a role change, an unload): the status follows.
    void this.refresh()
  }

  private bind(sock: AppSocket | null): void {
    if (this.boundTo === sock) return
    this.boundTo?.off('board:mine', this.onMine)
    this.boundTo?.off('board:team', this.onTeam)
    this.boundTo?.off('board:ticket', this.onTicket)
    this.boundTo?.off('board:spotlight', this.onSpotlight)
    this.boundTo?.off('board:proposals', this.onProposals)
    this.boundTo?.off('board:standup', this.onStandup)
    this.boundTo?.off('board:standup-host', this.onStandupHost)
    this.boundTo?.off('board:health', this.onHealth)
    this.boundTo?.off('board:health-team', this.onHealthTeam)
    this.boundTo = sock
    sock?.on('board:mine', this.onMine)
    sock?.on('board:team', this.onTeam)
    sock?.on('board:ticket', this.onTicket)
    sock?.on('board:spotlight', this.onSpotlight)
    sock?.on('board:proposals', this.onProposals)
    sock?.on('board:standup', this.onStandup)
    sock?.on('board:standup-host', this.onStandupHost)
    sock?.on('board:health', this.onHealth)
    sock?.on('board:health-team', this.onHealthTeam)
  }

  private call<T>(send: (sock: AppSocket, done: (r: AckResult<T>) => void) => void): Promise<AckResult<T>> {
    return new Promise((resolve) => {
      const sock = this.room.socket.get()
      if (!sock?.connected) {
        resolve({ ok: false, error: { code: 'offline', message: 'Not connected to the meeting.' } })
        return
      }
      const timer = setTimeout(() => resolve({ ok: false, error: { code: 'timeout', message: 'That took too long. Try again.' } }), WAIT_MS)
      send(sock, (r) => {
        clearTimeout(timer)
        resolve(r)
      })
    })
  }

  /** Ask what this person may do here. Quiet: a refusal just means nothing is offered. */
  async refresh(): Promise<void> {
    if (this.room.state.get().phase !== 'joined') return
    const res = await this.call<BoardStatus>((s, done) => s.emit('board:status', done))
    // An answer that arrives after the person has left must not bring the offer back.
    if (res.ok && this.room.state.get().phase === 'joined') {
      // Items already made into tickets (by this host or another): known by key, with nothing to undo from here.
      const known = this.store.get().madeTickets
      const made = { ...known }
      for (const [item, key] of Object.entries(res.data.made ?? {})) made[item] = known[item] ?? { key, actionId: null, undoable: false }
      patch(this.store, { status: res.data, madeTickets: made })
    }
  }

  /** Load a team's board for the meeting. True when it was loaded. */
  async load(teamId: string): Promise<boolean> {
    if (this.store.get().busy) return false
    patch(this.store, { busy: true, error: null })
    try {
      const res = await this.call<TeamBoardLive>((s, done) => s.emit('board:load', { teamId }, done))
      if (!res.ok) {
        patch(this.store, { error: res.error.message || 'Could not load the board.' })
        return false
      }
      patch(this.store, { team: res.data })
      await this.refresh()
      return true
    } finally {
      patch(this.store, { busy: false })
    }
  }

  /** Take the board away from everyone. True when it was. */
  async unload(): Promise<boolean> {
    if (this.store.get().busy) return false
    patch(this.store, { busy: true, error: null })
    try {
      const res = await this.call<void>((s, done) => s.emit('board:unload', done))
      if (!res.ok) {
        patch(this.store, { error: res.error.message || 'Could not take the board away.' })
        return false
      }
      patch(this.store, { team: null, mine: null })
      await this.refresh()
      return true
    } finally {
      patch(this.store, { busy: false })
    }
  }

  /** Put one ticket away. */
  dismissTicket(id: string): void {
    patch(this.store, { tickets: this.store.get().tickets.filter((t) => t.id !== id) })
  }

  /**
   * A host who may see the team: show a ticket to its owner (a card, to them alone). What happened comes
   * back to say, for the host: sent, or why not (the owner is not in the meeting, there is none).
   */
  async mention(key: string): Promise<MentionResult | null> {
    patch(this.store, { error: null })
    const res = await this.call<MentionResult>((s, done) => s.emit('board:mention', { key }, done))
    if (!res.ok) {
      patch(this.store, { error: res.error.message || 'Could not show that ticket.' })
      return null
    }
    return res.data
  }

  /** Put a ticket on screen for everyone in the meeting (null takes it down). True when it was done. */
  async spotlightTicket(key: string | null): Promise<boolean> {
    patch(this.store, { error: null })
    const res = await this.call<void>((s, done) => s.emit('board:spotlight', { key }, done))
    if (!res.ok) {
      patch(this.store, { error: res.error.message || 'Could not show that ticket.' })
      return false
    }
    return true
  }

  /**
   * Open a ticket in the call: its description, comments and sub-tasks, read live from Jira. Only your
   * own tickets (and, for a host who may see the team, the board's), so nobody leaves the call to read one.
   */
  async openTicket(key: string): Promise<void> {
    patch(this.store, { detail: { key, loading: true, data: null, error: null } })
    const res = await this.call<TicketView>((s, done) => s.emit('board:ticket-detail', { key }, done))
    // Closed, or another opened, while it was being read: leave that be.
    if (this.store.get().detail?.key !== key) return
    patch(this.store, {
      detail: res.ok
        ? { key, loading: false, data: res.data, error: null }
        : { key, loading: false, data: null, error: res.error.message || 'Could not open that ticket.' },
    })
  }

  closeTicket(): void {
    patch(this.store, { detail: null, actionError: null })
  }

  /**
   * Change the open ticket from the call: its owner, or a manager of the team. Applied at once; what it
   * did comes back to say (and to undo). The ticket is shown as it is afterwards.
   */
  async act(key: string, action: TicketAction): Promise<boolean> {
    if (this.store.get().acting) return false
    patch(this.store, { acting: true, actionError: null })
    try {
      const res = await this.call<ActResult>((s, done) => s.emit('board:act', { key, action }, done))
      if (!res.ok) {
        patch(this.store, { actionError: res.error.message || 'That change did not go through.' })
        return false
      }
      this.took(key, res.data)
      return true
    } finally {
      patch(this.store, { acting: false })
    }
  }

  /** Take back the last change, if it can be. */
  async undo(): Promise<boolean> {
    const change = this.store.get().lastChange
    if (!change || !change.undoable || this.store.get().acting) return false
    patch(this.store, { acting: true, actionError: null })
    try {
      const res = await this.call<ActResult>((s, done) => s.emit('board:undo', { id: change.id }, done))
      if (!res.ok) {
        patch(this.store, { actionError: res.error.message || 'That could not be undone.', lastChange: null })
        return false
      }
      patch(this.store, { lastChange: null })
      this.took(res.data.ticket.card.key, res.data, false)
      return true
    } finally {
      patch(this.store, { acting: false })
    }
  }

  /** What a change (or an undo) left: the ticket as it is now, and, for a change, the bar to undo it. */
  private took(key: string, r: ActResult, remember = true): void {
    const open = this.store.get().detail
    patch(this.store, {
      ...(remember ? { lastChange: { id: r.id, label: r.label, undoable: r.undoable, at: Date.now() } } : {}),
      // Only if that ticket is still the one on screen.
      ...(open?.key === key ? { detail: { key, loading: false, data: r.ticket, error: null } } : {}),
    })
  }

  /** A host who leads the team: switch the suggested updates on or off for this meeting. */
  async setSuggestions(on: boolean): Promise<boolean> {
    patch(this.store, { error: null })
    const res = await this.call<ProposalsStatus>((s, done) => s.emit('board:proposals-set', { on }, done))
    if (!res.ok) {
      patch(this.store, { error: res.error.message || 'Could not change that.' })
      return false
    }
    await this.refresh()
    return true
  }

  /**
   * Decide on a suggested update: approve it (maybe after changing the words, the points or the date) or
   * dismiss it. An approval makes the change under this person's name, like their own click, and can be
   * undone from the bar.
   */
  async decide(id: string, decision: ProposalDecisionPayload['decision'], edit?: ProposalDecisionPayload['edit']): Promise<boolean> {
    if (this.store.get().deciding) return false
    patch(this.store, { deciding: id, proposalError: null, proposalNote: null })
    try {
      const res = await this.call<ProposalDecisionResult>((s, done) =>
        s.emit('board:proposal', { id, decision, ...(edit ? { edit } : {}) }, done),
      )
      if (!res.ok) {
        patch(this.store, { proposalError: res.error.message || 'That did not go through.' })
        return false
      }
      const w = res.data.waiting
      patch(this.store, {
        proposals: res.data.proposals,
        proposalNote: w ? (w.told ? `Told ${w.told} they are being waited on.` : w.waitingOn ? `${w.waitingOn} is not clearly someone in this meeting, so nobody was told.` : null) : null,
      })
      if (res.data.result) this.took(res.data.result.ticket.card.key, res.data.result)
      return true
    } finally {
      patch(this.store, { deciding: null })
    }
  }

  /**
   * A host who leads the team: make a Jira ticket from one of the meeting's action items. The click is the approval.
   * `opts` may change the title and the person (a Jira account id, or null for nobody). True when it was made.
   */
  async makeTicket(itemId: string, opts: { summary?: string; assigneeAccountId?: string | null } = {}): Promise<boolean> {
    if (this.store.get().making) return false
    patch(this.store, { making: itemId, makeError: null })
    try {
      const res = await this.call<MadeTicket>((s, done) =>
        s.emit(
          'board:make-ticket',
          { itemId, ...(opts.summary ? { summary: opts.summary } : {}), ...(opts.assigneeAccountId !== undefined ? { assigneeAccountId: opts.assigneeAccountId } : {}) },
          done,
        ),
      )
      if (!res.ok) {
        patch(this.store, { makeError: res.error.message || 'Could not make that ticket.' })
        return false
      }
      patch(this.store, { madeTickets: { ...this.store.get().madeTickets, [itemId]: { key: res.data.key, actionId: res.data.actionId, undoable: res.data.undoable } } })
      return true
    } finally {
      patch(this.store, { making: null })
    }
  }

  /** Take back a ticket made from an action item (it is deleted), soon after making it. True when it was. */
  async unmakeTicket(itemId: string): Promise<boolean> {
    const made = this.store.get().madeTickets[itemId]
    if (!made?.actionId || !made.undoable || this.store.get().making) return false
    patch(this.store, { making: itemId, makeError: null })
    try {
      const res = await this.call<{ key: string }>((s, done) => s.emit('board:unmake-ticket', { id: made.actionId! }, done))
      if (!res.ok) {
        patch(this.store, { makeError: res.error.message || 'Could not take that back.' })
        return false
      }
      const rest = { ...this.store.get().madeTickets }
      delete rest[itemId]
      patch(this.store, { madeTickets: rest })
      return true
    } finally {
      patch(this.store, { making: null })
    }
  }

  clearMakeError(): void {
    patch(this.store, { makeError: null })
  }

  /** Read the brief: what the board has to say before the stand-up. A host who leads the team. */
  async openBrief(): Promise<boolean> {
    if (this.store.get().briefBusy) return false
    patch(this.store, { briefBusy: true, briefError: null })
    try {
      const res = await this.call<BriefView>((s, done) => s.emit('board:brief', done))
      if (!res.ok) {
        patch(this.store, { briefError: res.error.message || 'Could not make the brief.' })
        return false
      }
      patch(this.store, { brief: res.data })
      return true
    } finally {
      patch(this.store, { briefBusy: false })
    }
  }

  closeBrief(): void {
    patch(this.store, { brief: null, briefError: null })
  }

  /** A host who leads the team: start walking the board, `secondsEach` seconds a turn. True when it started. */
  async startStandup(secondsEach?: number): Promise<boolean> {
    return this.standupCall((s, done) => s.emit('board:standup-start', secondsEach ? { secondsEach } : {}, done))
  }

  /** Give the floor to the next person who is here. */
  nextSpeaker(): Promise<boolean> {
    return this.standupCall((s, done) => s.emit('board:standup-next', done))
  }

  /** Give the floor to one person, in or out of order. */
  giveFloor(accountId: string): Promise<boolean> {
    return this.standupCall((s, done) => s.emit('board:standup-jump', { accountId }, done))
  }

  /** End the stand-up. */
  stopStandup(): Promise<boolean> {
    return this.standupCall((s, done) => s.emit('board:standup-stop', done))
  }

  private async standupCall(send: (sock: AppSocket, done: (r: AckResult<void>) => void) => void): Promise<boolean> {
    if (this.store.get().busy) return false
    patch(this.store, { busy: true, error: null })
    try {
      const res = await this.call<void>(send)
      if (!res.ok) {
        patch(this.store, { error: res.error.message || 'That did not go through.' })
        return false
      }
      return true
    } finally {
      patch(this.store, { busy: false })
    }
  }

  /** Open your own board health: the numbers on your own tickets, with why. Sent to you alone. */
  async openHealth(): Promise<boolean> {
    if (this.store.get().healthBusy) return false
    patch(this.store, { healthBusy: true, healthMessage: null })
    try {
      const res = await this.call<MyHealthView>((s, done) => s.emit('board:health-mine', done))
      if (!res.ok) {
        patch(this.store, { healthMessage: res.error.message || 'Could not open board health.' })
        return false
      }
      patch(this.store, { health: res.data })
      return true
    } finally {
      patch(this.store, { healthBusy: false })
    }
  }

  /** Put your own board health away (the host can show it again, and you can open it again). */
  closeHealth(): void {
    patch(this.store, { health: null })
  }

  /**
   * A host who leads the team: show each person their OWN board health, or one person's (by their Jira
   * account). Each is sent their own view; the host is not sent anyone's by this. What it came to is said.
   */
  async showHealth(accountId?: string): Promise<HealthShowResult | null> {
    if (this.store.get().healthBusy) return null
    patch(this.store, { healthBusy: true, healthMessage: null })
    try {
      const res = await this.call<HealthShowResult>((s, done) => s.emit('board:health-show', accountId ? { accountId } : {}, done))
      if (!res.ok) {
        patch(this.store, { healthMessage: res.error.message || 'Could not show board health.' })
        return null
      }
      const { delivered, notHere } = res.data
      patch(this.store, {
        healthMessage: notHere.length
          ? `${notHere.join(', ')} is not in the meeting.`
          : delivered.length
            ? `Shown to ${delivered.join(', ')}, each their own.`
            : 'Nobody here is linked to Jira, so there was nothing to show.',
      })
      return res.data
    } finally {
      patch(this.store, { healthBusy: false })
    }
  }

  /** Open the team's board health: whoever leads the team, or the one person the host named. */
  async openTeamHealth(): Promise<boolean> {
    if (this.store.get().healthBusy) return false
    patch(this.store, { healthBusy: true, healthMessage: null })
    try {
      const res = await this.call<TeamHealthView>((s, done) => s.emit('board:health-team-open', done))
      if (!res.ok) {
        patch(this.store, { healthMessage: res.error.message || 'Could not open the team’s board health.' })
        return false
      }
      patch(this.store, { healthTeam: res.data })
      return true
    } finally {
      patch(this.store, { healthBusy: false })
    }
  }

  closeTeamHealth(): void {
    patch(this.store, { healthTeam: null })
  }

  /** A host who leads the team: name the one person who may also see the team view in this meeting, or withdraw it (`null`). */
  async setHealthLead(userId: string | null): Promise<boolean> {
    if (this.store.get().healthBusy) return false
    patch(this.store, { healthBusy: true, healthMessage: null })
    try {
      const res = await this.call<HealthAccessResult>((s, done) => s.emit('board:health-lead', { userId }, done))
      if (!res.ok) {
        patch(this.store, { healthMessage: res.error.message || 'Could not change that.' })
        return false
      }
      const status = this.store.get().status
      if (status) patch(this.store, { status: { ...status, health: { ...status.health, lead: res.data.lead, candidates: res.data.candidates } } })
      return true
    } finally {
      patch(this.store, { healthBusy: false })
    }
  }

  clearHealthMessage(): void {
    patch(this.store, { healthMessage: null })
  }

  clearProposalError(): void {
    patch(this.store, { proposalError: null })
  }

  clearProposalNote(): void {
    patch(this.store, { proposalNote: null })
  }

  /** Put the message about a refused change away. */
  clearActionError(): void {
    patch(this.store, { actionError: null })
  }

  /** Put the undo bar away. */
  dismissChange(): void {
    patch(this.store, { lastChange: null })
  }

  clearError(): void {
    patch(this.store, { error: null })
  }

  private reset(): void {
    patch(this.store, { mine: null, team: null, status: null, tickets: [], spotlight: null, detail: null, proposals: [], deciding: null, proposalError: null, proposalNote: null, health: null, healthTeam: null, healthBusy: false, healthMessage: null, madeTickets: {}, making: null, makeError: null, brief: null, briefBusy: false, briefError: null, standup: null, yourTurn: false, standupHost: null, lastChange: null, acting: false, actionError: null, attention: 0, busy: false, error: null })
  }
}
